import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { QrCodeIcon, ShieldCheckIcon, ShoppingCartIcon, ArchiveBoxIcon, ExclamationTriangleIcon, CubeIcon } from '@heroicons/react/24/outline'
import { track } from '../../utils/analytics'

const panelStats = [
  { key: 'unitsSold', icon: ShoppingCartIcon, value: '8,420' },
  { key: 'accuracy', icon: ShieldCheckIcon, value: '99.6%' },
  { key: 'skus', icon: CubeIcon, value: '1,240' },
]

const panelActivity = [
  { key: 'order', icon: ShoppingCartIcon, live: true },
  { key: 'restock', icon: ArchiveBoxIcon },
  { key: 'lowStock', icon: ExclamationTriangleIcon },
  { key: 'cycleCount', icon: QrCodeIcon },
]

export default function HeroSectionLanding() {

  const { t } = useTranslation('hero')

  return (

    <div className="">

        {/* Hero section */}
        <div className="isolate relative flex flex-col overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-electric-700 pt-14 lg:min-h-screen">
          <div
            className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
            aria-hidden="true"
          >
            <div
              className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[50rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-electric-400 to-neon-300 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[100rem]"
              style={{
                clipPath:
                  'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
              }}
            />
          </div>
          <div className="flex flex-1 items-center py-10 sm:py-12 md:py-14 lg:py-16">
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:px-8">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-neon-300">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-neon-300 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-neon-300"></span>
                  </span>
                  {t('badge')}
                </span>
                <h1 className="mt-6 text-4xl font-bold tracking-tight text-white sm:text-6xl">
                  {t('title.prefix')}{' '}
                  <span className="text-neon-300">{t('title.highlight')}</span> {t('title.suffix')}
                </h1>
                <p className="mt-8 text-xl leading-9 text-white/70">
                  {t('description')}
                </p>
                <div className="mt-10 flex flex-row flex-wrap items-start gap-3 sm:gap-4">
                  <Link
                    to="/contact"
                    state="cotizar-equipo"
                    onClick={() => track('hero_primary_cta_click', { variant: 'a' })}
                    className="rounded-md bg-neon-300 px-4 py-2.5 text-sm font-semibold text-navy-900 shadow-sm transition hover:bg-neon-400 sm:px-6 sm:py-4 sm:text-lg"
                  >
                    {t('ctaPrimary')} <span aria-hidden="true">&rarr;</span>
                  </Link>
                  <Link
                    to={{ pathname: '/', hash: '#casos-de-exito' }}
                    onClick={() => track('hero_secondary_cta_click', { variant: 'a' })}
                    className="rounded-md border border-white/30 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10 sm:px-6 sm:py-4 sm:text-lg"
                  >
                    {t('ctaSecondary')}
                  </Link>
                </div>
              </div>
              <div className="relative hidden lg:block">
                <div
                  className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-br from-electric-400/30 to-neon-300/20 blur-2xl"
                  aria-hidden="true"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-white/10 shadow-2xl backdrop-blur-xl">
                  <div className="flex items-center gap-2 border-b border-white/10 px-6 py-4 sm:px-8">
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                    <p className="ml-2 text-sm font-semibold text-white">{t('panel.title')}</p>
                    <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-neon-300">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-neon-300 opacity-75"></span>
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-neon-300"></span>
                      </span>
                      {t('panel.live')}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 divide-x divide-white/10 border-b border-white/10">
                    {panelStats.map((stat) => (
                      <div key={stat.key} className="px-3 py-5 text-center sm:px-4">
                        <stat.icon className="mx-auto h-5 w-5 text-neon-300" aria-hidden="true" />
                        <p className="mt-2 text-xl font-bold text-white sm:text-2xl">{stat.value}</p>
                        <p className="mt-1 text-[11px] leading-tight text-white/50 sm:text-xs">{t(`panel.stats.${stat.key}`)}</p>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2.5 px-4 py-5 sm:px-6">
                    <p className="px-1 text-xs font-semibold uppercase tracking-wide text-white/40">{t('panel.recentActivity')}</p>
                    {panelActivity.map((item) => (
                      <div key={item.key} className="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2.5">
                        <span className="relative flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white/10">
                          <item.icon className="h-4 w-4 text-neon-300" aria-hidden="true" />
                          {item.live && (
                            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-ping rounded-full bg-neon-300"></span>
                          )}
                        </span>
                        <p className="flex-1 text-sm text-white/90">{t(`panel.activity.${item.key}.text`)}</p>
                        <span className="flex-none text-xs text-white/40">{t(`panel.activity.${item.key}.time`)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-50rem)]"
            aria-hidden="true"
          >
            <div
              className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-navy-400 to-electric-300 opacity-25 sm:left-[calc(50%+36rem)] sm:w-[100rem]"
              style={{
                clipPath:
                  'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
              }}
            />
          </div>
        </div>
    </div>
  )
}
